import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Check, User, Hotel, Car, MapPin, Package, DollarSign, FileText, CheckCircle, Clipboard } from "lucide-react";

interface PackageProgressStepperProps {
  currentStep: number;
  completedSteps: number[];
  onStepClick: (step: number) => void;
  isStepRequired: (step: number) => boolean;
}

const steps = [
  {
    id: 1,
    title: "معلومات العميل",
    icon: User,
    description: "بيانات العميل الأساسية"
  },
  {
    id: 2,
    title: "معلومات العرض",
    icon: Clipboard,
    description: "تفاصيل الرحلة ومعلومات العرض السياحي"
  },
  {
    id: 3,
    title: "الفنادق والإقامة",
    icon: Hotel,
    description: "تفاصيل الحجوزات الفندقية"
  },
  {
    id: 4,
    title: "المواصلات",
    icon: Car,
    description: "وسائل النقل والمواصلات"
  },
  {
    id: 5,
    title: "الجولات السياحية",
    icon: MapPin,
    description: "الأنشطة والجولات المقترحة"
  },
  {
    id: 6,
    title: "شمول",
    icon: CheckCircle,
    description: "ما يشمله وما لا يشمله البرنامج"
  },
  {
    id: 7,
    title: "الخدمات المشمولة",
    icon: Package,
    description: "الخدمات الإضافية والمزايا"
  },
  {
    id: 8,
    title: "التسعير والعروض",
    icon: DollarSign,
    description: "تفاصيل الأسعار والخصومات"
  },
  {
    id: 9,
    title: "ملخص العرض",
    icon: FileText,
    description: "مراجعة نهائية وإنشاء PDF"
  }
];

export const PackageProgressStepper = ({
  currentStep,
  completedSteps,
  onStepClick,
  isStepRequired
}: PackageProgressStepperProps) => {
  const progress = (currentStep / steps.length) * 100;

  return (
    <Card className="fixed bottom-0 left-0 right-0 z-50 shadow-lg border-t bg-white/95 backdrop-blur-sm">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="flex items-center gap-3">
            <div className="text-sm font-medium text-muted-foreground min-w-fit">
              الخطوة {currentStep} من {steps.length}
            </div>
            <Progress value={progress} className="flex-1" />
            <div className="text-sm font-medium text-muted-foreground min-w-fit">
              {Math.round(progress)}% مكتمل
            </div>
          </div>

          {/* Steps List */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {steps.map((step) => {
              const isRequired = isStepRequired(step.id);
              const isCompleted = completedSteps.includes(step.id);
              const isCurrent = currentStep === step.id;
              const isClickable = isRequired && onStepClick;

              if (!isRequired) return null;

              return (
                <div
                  key={step.id}
                  onClick={() => isClickable && onStepClick(step.id)}
                  className={`
                    flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium min-w-fit transition-all duration-200
                    ${isCurrent 
                      ? 'bg-primary text-white shadow-md' 
                      : isCompleted 
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }
                    ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
                  `}
                >
                  <div className="flex-shrink-0">
                    {isCompleted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <step.icon className="w-4 h-4" />
                    )}
                  </div>
                  <span className="whitespace-nowrap">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};